import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead, { BreadcrumbSchema } from '@/components/feature/SEOHead';

const breadcrumbItems = [
  { name: '首页', url: '/' },
  { name: '关于我们', url: '/about' },
];

export default function AboutPage() {
  return (
    <>
      <SEOHead
        title="关于我们 - 星辰影院 | 品牌介绍、口碑评价、联系方式"
        description="星辰影院致力于为广大影迷提供最新、最全、最高清的电影资源。了解星辰影院的品牌故事、用户口碑评价，以及联系方式。"
        keywords="星辰影院,关于我们,品牌介绍,联系方式,电影网站"
        canonicalUrl="https://www.xingchencinema.com/about"
        ogTitle="关于我们 - 星辰影院"
        ogDescription="了解星辰影院的品牌故事与使命，查看用户口碑评价。"
      />

      <BreadcrumbSchema items={breadcrumbItems} />

      <Navbar />

      <main className="pt-16">
        <section className="bg-background-100 py-6 md:py-8" aria-label="面包屑导航">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-1 text-sm text-foreground-500" aria-label="Breadcrumb">
              {breadcrumbItems.map((item, idx) => (
                <span key={idx} className="flex items-center gap-1">
                  {idx > 0 && (
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-s-line text-xs"></i>
                    </span>
                  )}
                  {idx < breadcrumbItems.length - 1 ? (
                    <Link to={item.url} className="hover:text-primary-500 transition-colors whitespace-nowrap">
                      {item.name}
                    </Link>
                  ) : (
                    <span className="text-foreground-800 font-medium">{item.name}</span>
                  )}
                </span>
              ))}
            </nav>
          </div>
        </section>

        <section className="py-10 md:py-16 bg-background-50" aria-label="品牌介绍">
          <div className="max-w-[860px] mx-auto px-4 md:px-6">
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-6 text-center">
              关于星辰影院
            </h1>
            <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-10 mb-10">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-4">
                我们的故事
              </h2>
              <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-7 md:leading-8">
                <p>
                  星辰影院成立于2020年，是一家专注于高清电影在线播放的综合影视平台。我们秉承"让每个人都能享受高品质观影体验"的使命，汇聚了来自全球的海量电影资源。
                </p>
                <p>
                  从经典老片到最新院线大片，从国产佳作到好莱坞巨制，从蓝光4K到杜比全景声——星辰影院始终致力于为广大影迷提供<strong>最全面、最高清、最流畅</strong>的在线观影服务。
                </p>
                <p>
                  我们的团队由一群热爱电影的技术工程师和内容运营专家组成，通过先进的视频编解码技术和CDN加速网络，确保每一位用户都能享受到<strong>不缓冲、不卡顿</strong>的极致观影体验。
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-10">
              {[
                { icon: 'ri-movie-2-line', label: '电影资源', value: '10,000+', desc: '海量影片库' },
                { icon: 'ri-user-heart-line', label: '服务用户', value: '500万+', desc: '累计观影人次' },
                { icon: 'ri-4k-line', label: '4K画质', value: '80%+', desc: '蓝光4K覆盖率' },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="bg-background-50 rounded-lg border border-background-200/70 p-6 text-center"
                >
                  <span className="w-10 h-10 mx-auto flex items-center justify-center mb-3">
                    <i className={`${stat.icon} text-2xl text-primary-500`}></i>
                  </span>
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">
                    {stat.value}
                  </div>
                  <p className="text-sm font-medium text-foreground-800 mb-1">{stat.label}</p>
                  <p className="text-xs text-foreground-500">{stat.desc}</p>
                </div>
              ))}
            </div>

            <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-10 mb-10">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-4">
                品牌使命
              </h2>
              <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-7 md:leading-8">
                <p>
                  <strong>让电影触手可及</strong>—— 我们相信，好的电影应该被更多人看到。无论你身处何地，使用何种设备，星辰影院都致力于为你打开一扇通向光影世界的大门。
                </p>
                <p>
                  <strong>品质至上</strong>—— 我们坚持为用户提供最高画质的观影体验。从蓝光到4K，从立体声到全景声，每一个细节都不妥协。
                </p>
                <p>
                  <strong>持续创新</strong>—— 技术团队持续优化播放引擎和推荐算法，确保你每次打开星辰影院，都能发现心仪的好电影。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-16 bg-background-100" aria-label="用户口碑">
          <div className="max-w-[860px] mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-8 text-center">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {[
                {
                  name: '电影爱好者小王',
                  comment: '星辰影院的4K画质真的太震撼了！而且播放流畅不卡顿，比很多收费平台体验都好。现在每天下班回家第一件事就是打开星辰影院。',
                  rating: 5,
                },
                {
                  name: '大学生小李',
                  comment: '作为一个电影狂热爱好者，星辰影院的资源真的超全！从经典老片到最新上映的都有，而且完全免费，太良心了！',
                  rating: 5,
                },
                {
                  name: '程序员老张',
                  comment: '技术做得很好，CDN加速效果明显。在手机上也能流畅播放4K，画质清晰，加载速度快，推荐给所有爱看电影的朋友。',
                  rating: 5,
                },
                {
                  name: '家庭主妇刘姐',
                  comment: '周末经常和家人一起在星辰影院看电影，分类很清楚，找片方便。孩子看动画片，我和老公看科幻片，一家人都能找到喜欢的。',
                  rating: 5,
                },
                {
                  name: '退休教师陈老师',
                  comment: '年纪大了出门看电影不方便，星辰影院让我在家就能看到各种好片子。界面简洁好操作，老年人也能轻松使用。',
                  rating: 5,
                },
                {
                  name: '摄影爱好者阿杰',
                  comment: '作为一个视觉控，星辰影院的画质真的让我惊艳。4K蓝光的色彩还原度和画面细节都是顶级的，每一帧都是一幅画。',
                  rating: 5,
                },
              ].map((review, idx) => (
                <div
                  key={idx}
                  className="bg-background-50 rounded-lg border border-background-200/70 p-5"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: review.rating }).map((_, i) => (
                      <span key={i} className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-star-fill text-primary-500 text-xs"></i>
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    "{review.comment}"
                  </p>
                  <p className="text-xs text-foreground-500 font-medium">—— {review.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-16 bg-background-50" aria-label="联系我们">
          <div className="max-w-[860px] mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-8 text-center">
              联系我们
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {[
                {
                  icon: 'ri-mail-line',
                  label: '电子邮箱',
                  value: 'contact@xingchencinema.com',
                  desc: '商务合作 / 内容反馈',
                },
                {
                  icon: 'ri-phone-line',
                  label: '客服热线',
                  value: '400-888-9999',
                  desc: '周一至周日 9:00-21:00',
                },
                {
                  icon: 'ri-map-pin-line',
                  label: '公司地址',
                  value: '北京市朝阳区',
                  desc: '影视产业园区A座18层',
                },
              ].map((contact) => (
                <div
                  key={contact.label}
                  className="bg-background-50 rounded-lg border border-background-200/70 p-6 text-center"
                >
                  <span className="w-10 h-10 mx-auto flex items-center justify-center mb-3">
                    <i className={`${contact.icon} text-2xl text-primary-500`}></i>
                  </span>
                  <h3 className="text-sm font-semibold text-foreground-800 mb-1">{contact.label}</h3>
                  <p className="text-sm font-medium text-primary-500 mb-1">{contact.value}</p>
                  <p className="text-xs text-foreground-500">{contact.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}